/**
 * Crafting Service - Fetch crafting blueprints, resources, and blueprint pools from API
 */

import axios from '../api/axios';

// === Types ===

export interface CraftingBlueprintListItem {
  id: number;
  recordName: string;
  displayName: string | null;
  blueprintType: string;
  category: string;
  subCategory: string | null;
  outputItemName: string | null;
  craftTimeSeconds: number;
  ingredientCount: number;
  imageUrl: string | null;
}

export interface CraftingIngredient {
  slotName: string;
  slotDisplayName: string | null;
  resourceName: string;
  resourceDisplayName: string | null;
  quantityScu: number;
  minQuality: number;
  miningType: string | null;
  rarity: string | null;
  location: string | null;
}

export interface CraftingStatModifier {
  slotName: string;
  statName: string;
  startQuality: number;
  endQuality: number;
  modifierAtStart: number;
  modifierAtEnd: number;
}

export interface ContractorSource {
  contractorName: string;
  missionName: string;
  missionTitle?: string;
  missionDescription?: string;
  missionDifficulty?: string;
  missionTimeMinutes?: number;
  requiresReputation?: string;
  systems?: string;
}

export interface BlueprintPoolSummary {
  poolId: number;
  faction: string;
  missionType: string;
  weight: number;
  contractors: ContractorSource[];
}

export interface CraftingBlueprintDetail {
  id: number;
  gameId: string;
  recordName: string;
  displayName: string | null;
  blueprintType: string;
  category: string;
  subCategory: string | null;
  outputItemGameId: string | null;
  outputItemName: string | null;
  craftTimeSeconds: number;
  dismantleTimeSeconds: number | null;
  dismantleEfficiency: number | null;
  ingredients: CraftingIngredient[];
  statModifiers: CraftingStatModifier[];
  availableFrom: BlueprintPoolSummary[];
  imageUrl: string | null;
}

export interface CraftingResource {
  id: number;
  name: string;
  displayName: string;
  description: string | null;
  thumbnailPath: string | null;
  miningType: string | null;
  rarity: string | null;
  location: string | null;
  qualityMin: number | null;
  qualityMax: number | null;
  qualityMean: number | null;
  refinedFromName: string | null;
  usedInBlueprintCount: number;
}

export interface BlueprintPoolEntry {
  blueprintId: number | null;
  blueprintName: string | null;
  blueprintDisplayName: string | null;
  outputItemName: string | null;
  category: string | null;
  imageUrl: string | null;
  weight: number;
}

export interface BlueprintPool {
  id: number;
  recordName: string;
  faction: string;
  missionType: string;
  entries: BlueprintPoolEntry[];
  contractors: ContractorSource[];
}

export interface CraftingStats {
  totalBlueprints: number;
  weaponBlueprints: number;
  armorBlueprints: number;
  totalResources: number;
  totalPools: number;
  categories: string[];
  factions: string[];
}

export interface BlueprintQueryParams {
  page?: number;
  pageSize?: number;
  category?: string;
  subCategory?: string;
  search?: string;
  resource?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface BlueprintsResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  items: CraftingBlueprintListItem[];
}

// === Service ===

class CraftingService {
  async getStats(): Promise<CraftingStats> {
    const response = await axios.get('/crafting/stats');
    return response.data;
  }

  async getBlueprints(params: BlueprintQueryParams = {}): Promise<BlueprintsResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.subCategory) queryParams.append('subCategory', params.subCategory);
    if (params.search) queryParams.append('search', params.search);
    if (params.resource) queryParams.append('resource', params.resource);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await axios.get(`/crafting/blueprints?${queryParams.toString()}`);
    return response.data;
  }

  async getBlueprintById(id: number): Promise<CraftingBlueprintDetail> {
    const response = await axios.get(`/crafting/blueprints/${id}`);
    return response.data;
  }

  async getResources(): Promise<CraftingResource[]> {
    const response = await axios.get('/crafting/resources');
    return response.data;
  }

  async getPools(faction?: string): Promise<BlueprintPool[]> {
    const params = faction ? `?faction=${encodeURIComponent(faction)}` : '';
    const response = await axios.get(`/crafting/pools${params}`);
    return response.data;
  }

  async getSubCategories(category?: string): Promise<string[]> {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await axios.get(`/crafting/subcategories${params}`);
    return response.data;
  }

  async searchBlueprints(query: string, limit: number = 20): Promise<CraftingBlueprintListItem[]> {
    const response = await axios.get(`/crafting/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  }

  // Planner persistence (requires auth)
  async getPlan(): Promise<SavedPlanEntry[]> {
    const response = await axios.get('/crafting/planner');
    return response.data;
  }

  async savePlan(entries: SavedPlanEntry[]): Promise<void> {
    await axios.put('/crafting/planner', entries);
  }

  async clearPlan(): Promise<void> {
    await axios.delete('/crafting/planner');
  }
}

export interface SavedPlanEntry {
  blueprintId: number;
  quantity: number;
  slotQualities: string; // JSON string
}

export const craftingService = new CraftingService();
